/**
 * 🧠 SMART BOOKING EDIT MODAL
 * Intelligent booking modification interface with real-time calculations,
 * automatic refunds, additional payments, and cancellation handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Button,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close,
  Calculate,
  Payment,
  Refresh,
  CheckCircle,
  Cancel,
  Edit,
  Schedule,
  Info,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { safeFormatDate } from '@/utils/filterUtils';
import { simplBookingService } from '@/services/bookingService';
import smartBookingService, {
  BookingDetails,
  ModificationResult,
  CancellationPolicy
} from '@/services/smartBookingService';

interface SmartEditBookingModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
  onSuccess: () => void;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '90%', md: '800px' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '95vh',
  overflowY: 'auto',
};

type OperationMode = 'edit' | 'cancel' | 'preview' | 'processing';

export const SmartEditBookingModal: React.FC<SmartEditBookingModalProps> = ({
  open,
  onClose,
  bookingId,
  onSuccess,
}) => {
  // State management
  const [originalBooking, setOriginalBooking] = useState<BookingDetails | null>(null);
  const [editedBooking, setEditedBooking] = useState<Partial<BookingDetails>>({});
  const [modificationResult, setModificationResult] = useState<ModificationResult | null>(null);
  const [cancellationPolicy, setCancellationPolicy] = useState<CancellationPolicy | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [operationMode, setOperationMode] = useState<OperationMode>('edit');
  const [showCancellationDialog, setShowCancellationDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [processingStep, setProcessingStep] = useState(0);
  const [detectedBookingType, setDetectedBookingType] = useState<'car-rental' | 'driver-hire'>('car-rental');

  // Load booking data when modal opens
  useEffect(() => {
    if (open && bookingId) {
      loadBookingData();
      // Clear any previous success messages
      setSuccess(null);
      setError(null);
    }
  }, [open, bookingId]);

  // Debounced calculation
  const debouncedCalculation = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          calculateModification();
        }, 500);
      };
    })(),
    [originalBooking, editedBooking]
  );

  // Auto-calculate when booking details change
  useEffect(() => {
    if (originalBooking && Object.keys(editedBooking).length > 0) {
      debouncedCalculation();
    }
  }, [editedBooking, originalBooking, debouncedCalculation]);

  const loadBookingData = async () => {
    setIsLoading(true);
    setError(null);
    setEditedBooking({});
    setModificationResult(null);
    
    try {
      const allBookings = await simplBookingService.getAllBookings();
      const foundBooking = allBookings.find((b: any) => b.id === bookingId);
      
      if (foundBooking) {
        // Detect booking type from the raw booking data
        const bookingType = foundBooking.service_category || foundBooking.serviceCategory || 'car-rental';
        setDetectedBookingType(bookingType === 'driver-hire' ? 'driver-hire' : 'car-rental');
        
        // Transform API booking to our BookingDetails format
        const bookingDetails: BookingDetails = {
          id: foundBooking.id,
          startDate: foundBooking.startDate,
          endDate: foundBooking.endDate,
          vehicleId: foundBooking.vehicle?.id || 'vehicle-1',
          customerId: foundBooking.customer?.id || foundBooking.user?.id || 'customer-1',
          originalAmount: foundBooking.amount || foundBooking.totalAmount || 0,
          paidAmount: foundBooking.amount || foundBooking.totalAmount || 0,
          serviceType: mapServiceType(foundBooking.serviceType || foundBooking.type),
          bookingType: foundBooking.bookingType === 'instant' ? 'instant' : 'request_to_book',
          status: foundBooking.status || 'confirmed',
          createdAt: foundBooking.createdAt || new Date().toISOString(),
          vehicleCategory: foundBooking.vehicleCategory || 'standard',
          location: foundBooking.location || 'Lagos',
          additionalServices: [],
          discounts: 0
        };
        
        setOriginalBooking(bookingDetails);
        
        // Load cancellation policy
        const policy = smartBookingService.calculateCancellationPolicy(bookingDetails);
        setCancellationPolicy(policy);
        
      } else {
        setError('Booking not found');
      }
    } catch (err) {
      setError('Failed to load booking details');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const mapServiceType = (type: string): BookingDetails['serviceType'] => {
    const mapping: { [key: string]: BookingDetails['serviceType'] } = {
      'self-drive': 'self_drive',
      'self_drive': 'self_drive',
      'with-driver': 'with_driver',
      'with_driver': 'with_driver',
      'chauffeur-driven': 'chauffeur_driven',
      'chauffeur_driven': 'chauffeur_driven',
      'rental': 'self_drive',
    };
    return mapping[type] || 'self_drive';
  };

  const calculateModification = async () => {
    if (!originalBooking || Object.keys(editedBooking).length === 0) return;
    
    setIsCalculating(true);
    try {
      const result = await smartBookingService.analyzeBookingModification(
        originalBooking,
        editedBooking
      );
      setModificationResult(result);
    } catch (err) {
      console.error('Calculation error:', err);
      setError('Failed to calculate modification');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleInputChange = (field: keyof BookingDetails, value: any) => {
    if (!originalBooking) return;
    
    // Convert datetime-local value to ISO string if needed
    let processedValue = value;
    if (field === 'startDate' || field === 'endDate') {
      processedValue = new Date(value).toISOString();
    }
    
    // Only store the change if it's different from the original
    if (processedValue !== originalBooking[field]) {
      setEditedBooking(prev => ({
        ...prev,
        [field]: processedValue
      }));
    } else {
      // Remove the field if it matches the original
      setEditedBooking(prev => {
        const newEdited = { ...prev };
        delete newEdited[field];
        return newEdited;
      });
    }
  };

  const formatDateForInput = (dateString: string): string => {
    try {
      return new Date(dateString).toISOString().slice(0, 16);
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  const getCurrentValue = (field: keyof BookingDetails) => {
    return editedBooking[field] !== undefined ? editedBooking[field] : originalBooking?.[field];
  };

  const handleSaveModification = async () => {
    if (!originalBooking || Object.keys(editedBooking).length === 0) {
      setError('No changes to apply');
      return;
    }
    
    // Allow override even if canModify is false (admin override)
    if (!modificationResult) {
      setError('Calculation required before applying changes');
      return;
    }
    
    setIsProcessing(true);
    setProcessingStep(0);
    setError(null);
    
    try {
      // Step 1: Validate modification
      setProcessingStep(1);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Process payments/refunds
      setProcessingStep(2);
      
      if (modificationResult.additionalPaymentRequired) {
        const paymentResult = await smartBookingService.processAdditionalPayment(
          originalBooking.id,
          modificationResult.additionalAmount,
          'Booking modification additional payment',
          'admin-user-123'
        );
        
        if (!paymentResult.success) {
          throw new Error(paymentResult.error || 'Payment failed');
        }
        
      }
      
      if (modificationResult.refundRequired) {
        const refundResult = await smartBookingService.processRefund(
          originalBooking.id,
          modificationResult.refundAmount,
          'Booking modification refund',
          'admin-user-123'
        );
        
        if (!refundResult.success) {
          throw new Error(refundResult.error || 'Refund failed');
        }
        
      }
      
      // Step 3: Update booking in system
      setProcessingStep(3);
      
      // Apply the changes to the booking
      const updatedBookingData = { ...originalBooking, ...editedBooking };
      
      try {
        // Update the booking in the backend
        const updateResult = await simplBookingService.updateBooking(originalBooking.id, {
          ...updatedBookingData,
          modifiedAt: new Date().toISOString(),
          modifiedBy: 'admin-user-123',
          modifications: {
            originalAmount: originalBooking.originalAmount,
            newAmount: modificationResult.pricingComparison.new.totalAmount,
            priceDifference: modificationResult.pricingComparison.difference,
            modificationFee: modificationResult.modificationFee,
            additionalPayment: modificationResult.additionalAmount,
            refundAmount: modificationResult.refundAmount,
            changes: editedBooking
          }
        });
        
        if (!updateResult.success) {
          throw new Error('Failed to update booking in system');
        }

        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (updateError) {
        console.error('Failed to update booking:', updateError);
        throw new Error('Failed to update booking in system');
      }
      
      // Step 4: Send notifications
      setProcessingStep(4);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSuccess('Booking modification completed successfully!');
      
      // Generate modification report
      const report = smartBookingService.generateModificationReport(
        originalBooking,
        modificationResult,
        'admin-user-123'
      );
      
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to process modification');
      console.error('Modification error:', err);
    } finally {
      setIsProcessing(false);
      setProcessingStep(0);
    }
  };

  const handleCancelBooking = async () => {
    if (!originalBooking || !cancellationPolicy) return;
    
    setIsProcessing(true);
    try {
      const result = await smartBookingService.processCancellation(
        originalBooking,
        'admin-user-123',
        cancellationReason
      );
      
      if (result.success) {
        setSuccess(`Booking cancelled successfully! ${result.refundId ? `Refund ID: ${result.refundId}` : ''}`);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Cancellation failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel booking');
    } finally {
      setIsProcessing(false);
      setShowCancellationDialog(false);
    }
  };

  const renderLoadingState = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
      <CircularProgress />
      <Typography sx={{ ml: 2 }}>Loading booking details...</Typography>
    </Box>
  );

  const renderProcessingSteps = () => {
    const steps = [
      'Validating Changes',
      'Processing Payments',
      'Updating Booking',
      'Sending Notifications'
    ];
    
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Stepper activeStep={processingStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  };

  const renderModificationSummary = () => {
    if (!modificationResult) return null;
    
    return (
      <Card sx={{ mt: 2, border: '1px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calculate /> Modification Summary
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Original Booking</Typography>
              <Typography variant="body2">Amount: ₦{modificationResult.pricingComparison.original.totalAmount.toLocaleString()}</Typography>
              <Typography variant="body2">Duration: {modificationResult.pricingComparison.original.durationDays} days</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>New Booking</Typography>
              <Typography variant="body2">Amount: ₦{modificationResult.pricingComparison.new.totalAmount.toLocaleString()}</Typography>
              <Typography variant="body2">Duration: {modificationResult.pricingComparison.new.durationDays} days</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Price Difference: 
                  <Chip
                    label={`₦${Math.abs(modificationResult.pricingComparison.difference).toLocaleString()}`}
                    color={modificationResult.pricingComparison.difference > 0 ? 'error' : 'success'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  + ₦{modificationResult.modificationFee.toLocaleString()} modification fee
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {modificationResult.additionalPaymentRequired && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Additional Payment Required</Typography>
              <Typography variant="body2">
                Customer needs to pay ₦{modificationResult.additionalAmount.toLocaleString()} to confirm this modification.
              </Typography>
            </Alert>
          )}
          
          {modificationResult.refundRequired && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Refund Will Be Processed</Typography>
              <Typography variant="body2">
                ₦{modificationResult.refundAmount.toLocaleString()} will be refunded to the customer's original payment method.
              </Typography>
            </Alert>
          )}
          
          <List dense sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Actions to be performed:</Typography>
            {modificationResult.actions.map((action, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {action.type === 'charge' && <Payment color="error" />}
                  {action.type === 'refund' && <Refresh color="success" />}
                  {action.type === 'update' && <Edit color="primary" />}
                  {action.type === 'notify' && <Info color="info" />}
                </ListItemIcon>
                <ListItemText
                  primary={action.description}
                  secondary={action.automated ? 'Automatic' : 'Manual approval required'}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const renderCancellationPolicy = () => {
    if (!cancellationPolicy) return null;
    
    const getSeverityColor = () => {
      switch (cancellationPolicy.cancellationTier) {
        case 'free': return 'success';
        case 'low_fee': return 'info';
        case 'medium_fee': return 'warning';
        case 'high_fee': return 'error';
        case 'no_refund': return 'error';
        default: return 'default';
      }
    };
    
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule /> Cancellation Policy
          </Typography>
          
          <Alert severity={getSeverityColor() as any} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">{cancellationPolicy.policyDetails}</Typography>
            <Typography variant="body2">
              Time until booking: {cancellationPolicy.hoursUntilBooking} hours
            </Typography>
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Cancellation Fee</Typography>
              <Typography variant="h6" color="error">
                ₦{cancellationPolicy.cancellationFee.toLocaleString()}
                <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                  ({Math.round(cancellationPolicy.cancellationFeePercentage * 100)}%)
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Refund Amount</Typography>
              <Typography variant="h6" color="success.main">
                ₦{cancellationPolicy.refundAmount.toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          {renderLoadingState()}
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              🧠 Smart Booking Management
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          {/* Mode selector */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant={operationMode === 'edit' ? 'contained' : 'outlined'}
              onClick={() => setOperationMode('edit')}
              startIcon={<Edit />}
            >
              Modify Booking
            </Button>
            <Button
              variant={operationMode === 'cancel' ? 'contained' : 'outlined'}
              onClick={() => setOperationMode('cancel')}
              startIcon={<Cancel />}
              color="error"
            >
              Cancel Booking
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {isProcessing && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              {renderProcessingSteps()}
            </Box>
          )}

          {operationMode === 'edit' && originalBooking && (
            <>
              {/* Current Booking Details */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Current Booking Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Booking Type:</strong> {detectedBookingType === 'driver-hire' ? 'Driver Hire' : 'Car Rental'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Duration:</strong> {safeFormatDate(originalBooking.startDate, 'PPp')} to {safeFormatDate(originalBooking.endDate, 'PPp')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Original Amount:</strong> ₦{originalBooking.originalAmount.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Service Type:</strong> {originalBooking.serviceType.replace('_', ' ')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Vehicle Category:</strong> {originalBooking.vehicleCategory}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Booking Edit Form */}
              <Typography variant="h6" gutterBottom>Make Changes</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="datetime-local"
                    value={formatDateForInput(getCurrentValue('startDate') as string)}
                    onChange={(e) => {
                      handleInputChange('startDate', e.target.value);
                      setTimeout(() => e.target.blur(), 100);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="datetime-local"
                    value={formatDateForInput(getCurrentValue('endDate') as string)}
                    onChange={(e) => {
                      handleInputChange('endDate', e.target.value);
                      setTimeout(() => e.target.blur(), 100);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Service Type"
                    value={getCurrentValue('serviceType')}
                    onChange={(e) => handleInputChange('serviceType', e.target.value)}
                  >
                    {detectedBookingType === 'driver-hire' ? (
                      <>
                        <MenuItem value="hourly">Hourly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem value="self_drive">Self Drive</MenuItem>
                        <MenuItem value="with_driver">With Driver</MenuItem>
                        <MenuItem value="chauffeur_driven">Chauffeur Driven</MenuItem>
                      </>
                    )}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Vehicle Category"
                    value={getCurrentValue('vehicleCategory')}
                    onChange={(e) => handleInputChange('vehicleCategory', e.target.value)}
                  >
                    {detectedBookingType === 'driver-hire' ? (
                      <>
                        <MenuItem value="sedan">Sedan</MenuItem>
                        <MenuItem value="suv">SUV</MenuItem>
                        <MenuItem value="van">Van</MenuItem>
                        <MenuItem value="bus">Bus</MenuItem>
                        <MenuItem value="luxury">Luxury</MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem value="economy">Economy</MenuItem>
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="premium">Premium</MenuItem>
                        <MenuItem value="luxury">Luxury</MenuItem>
                      </>
                    )}
                  </TextField>
                </Grid>
              </Grid>

              {isCalculating && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">Calculating changes...</Typography>
                </Box>
              )}

              {/* DEBUG INFO */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  DEBUG: editedBooking keys: {Object.keys(editedBooking).length} | 
                  modificationResult: {modificationResult ? 'exists' : 'null'} | 
                  canModify: {modificationResult?.canModify ? 'true' : 'false'} | 
                  isProcessing: {isProcessing ? 'true' : 'false'}
                </Typography>
              </Alert>

              {renderModificationSummary()}

              {modificationResult && !modificationResult.canModify && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Modification Not Allowed</Typography>
                  <Typography variant="body2">
                    This booking cannot be modified (likely due to timing restrictions). Please contact the customer to cancel and create a new booking.
                  </Typography>
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button onClick={onClose} disabled={isProcessing}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveModification}
                  disabled={
                    isProcessing || 
                    Object.keys(editedBooking).length === 0
                  }
                  color={modificationResult?.canModify === false ? 'warning' : 'primary'}
                  startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {isProcessing 
                    ? 'Processing...' 
                    : Object.keys(editedBooking).length === 0 
                      ? 'No Changes Made' 
                      : modificationResult?.canModify === false
                        ? 'Cannot Modify (Too Late)'
                        : `Apply Changes${modificationResult ? ` (${Object.keys(editedBooking).length} change${Object.keys(editedBooking).length === 1 ? '' : 's'})` : ''}`}
                </Button>
              </Box>
            </>
          )}

          {operationMode === 'cancel' && (
            <>
              {renderCancellationPolicy()}
              
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Cancellation Reason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Please provide a reason for cancellation..."
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button onClick={onClose} disabled={isProcessing}>
                  Keep Booking
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setShowCancellationDialog(true)}
                  disabled={!cancellationReason.trim() || isProcessing}
                  startIcon={<Cancel />}
                >
                  Cancel Booking
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Cancellation Confirmation Dialog */}
      <Dialog open={showCancellationDialog} onClose={() => setShowCancellationDialog(false)}>
        <DialogTitle>Confirm Booking Cancellation</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel this booking?
          </Typography>
          {cancellationPolicy && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Cancellation fee: ₦{cancellationPolicy.cancellationFee.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Refund amount: ₦{cancellationPolicy.refundAmount.toLocaleString()}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancellationDialog(false)}>Keep Booking</Button>
          <Button
            onClick={handleCancelBooking}
            color="error"
            variant="contained"
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <Cancel />}
          >
            {isProcessing ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};